import React, { useEffect, useMemo, useState } from "react";
import { StrokedText } from "../ui/text/stroked-text";
import { PokemonSprite } from "../pokemon/pokemon-sprite";
import { useDuel } from "@/providers/duel-provider";
import { AnimatedVFX } from "../vfx/animated-vfx";
import { useUser } from "@/providers/user-provider";
import { Direction } from "types/battle-engine/battle";
import { CountryFlag } from "../ui/country-flag/country-flag";
import { isXsScreen } from "@/utils/screen-size-check";
import { CompleteBattleLog } from "types/battle-engine";

interface BattleCompletedTransitionProps {
  winnerName: string;
  playerScore: number;
  opponentScore: number;
  isPlayerWinner: boolean;
  isDraw?: boolean;
  onReady: () => void;
}

/**
 * Battle completion screen showing the winner and current score.
 * After 2-3 seconds, displays "waiting for opponent" until next step.
 */
export const BattleCompletedTransition: React.FC<
  BattleCompletedTransitionProps
> = ({
  winnerName,
  playerScore,
  opponentScore,
  isPlayerWinner,
  isDraw = false,
  onReady,
}) => {
  const [phase, setPhase] = useState<"impact" | "waiting">("impact");
  const [showImpact, setShowImpact] = useState(false);
  const [battleLog, setBattleLog] = useState<CompleteBattleLog | null>(null);
  const { duelState } = useDuel();
  const { playerCountry, username } = useUser();

  // Use last_battle if current_battle is null (which it is when status is battle_completed)
  const battleData = duelState.current_battle || duelState.last_battle;

  const { yourCurrentTeam, opponentCurrentTeam } = useMemo(() => {
    const yourTeamIds = battleData?.your_team || [];
    const opponentTeamIds = battleData?.opponent_team || [];

    return {
      yourCurrentTeam: duelState.your_team.filter((pokemon) =>
        yourTeamIds.includes(pokemon.id),
      ),
      opponentCurrentTeam: duelState.opponent_team.filter((pokemon) =>
        opponentTeamIds.includes(pokemon.id),
      ),
    };
  }, [duelState.your_team, duelState.opponent_team, battleData]);

  // Extract battle stats for each Pokemon
  const pokemonStats = useMemo(() => {
    if (!battleLog?.stats)
      return {
        stats: [],
        minMax: {
          damage_done: { min: 0, max: 0 },
          healing_done: { min: 0, max: 0 },
        },
      };

    const stats = battleLog.stats;
    const allPokemon = [...yourCurrentTeam, ...opponentCurrentTeam];

    const pokemonData = allPokemon.map((pokemon) => {
      const pokemonStats = stats[pokemon.id.toString()] || {
        damage_done: 0,
        healing_done: 0,
      };

      return {
        pokemon,
        stats: pokemonStats,
        isPlayerPokemon: yourCurrentTeam.some((p) => p.id === pokemon.id),
      };
    });

    // Calculate min/max values for scaling
    const damageDoneValues = pokemonData.map((p) => p.stats.damage_done);
    const healingDoneValues = pokemonData.map((p) => p.stats.healing_done);

    const minMax = {
      damage_done: {
        min: Math.max(0, Math.min(...damageDoneValues) * 0.8), // 20% less than actual min
        max: Math.max(...damageDoneValues),
      },
      healing_done: {
        min: Math.max(0, Math.min(...healingDoneValues) * 0.8),
        max: Math.max(...healingDoneValues),
      },
    };

    const sortedPokemonData = pokemonData.sort((a, b) => {
      // Sort by total contribution (damage done + healing done - damage taken)
      const scoreA = a.stats.damage_done + a.stats.healing_done;
      const scoreB = b.stats.damage_done + b.stats.healing_done;
      return scoreB - scoreA;
    });

    return { stats: sortedPokemonData, minMax };
  }, [battleLog, yourCurrentTeam, opponentCurrentTeam]);

  useEffect(() => {
    const timers: number[] = [];

    // Initial impact
    timers.push(
      setTimeout(() => {
        setShowImpact(true);
      }, 50),
    );

    // Switch to waiting phase after 3 seconds (visual only)
    timers.push(
      setTimeout(() => {
        setPhase("waiting");
      }, 3000),
    );

    // Note: No auto-dismiss timer. We wait for duel_round_started message
    // which will change the status from "battle_completed" to "waiting_for_step"

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, []); // Remove onReady dependency since we don't auto-dismiss

  // Watch for status changes and dismiss when step_started is received
  // (status changes from "new_round_starting" to "waiting_for_step" or "in_step")
  useEffect(() => {
    // Keep showing during these phases
    // We wait for "waiting_for_step" or "in_step" (which comes from "step_started" message) to dismiss
    const keepShowing =
      duelState.status === "battle_completed" ||
      duelState.status === "in_battle" ||
      duelState.status === "new_round_starting";

    if (!keepShowing) {
      onReady();
    }
  }, [duelState.status, onReady]);

  // Fetch battle log when component mounts
  useEffect(() => {
    const fetchBattleLog = async () => {
      if (!battleData?.battle_log_url) return;

      try {
        const response = await fetch(battleData.battle_log_url);
        const log = (await response.json()) as CompleteBattleLog;
        setBattleLog(log);
      } catch (error) {
        console.error("Failed to fetch battle log:", error);
      }
    };

    fetchBattleLog();
  }, [battleData?.battle_log_url]);

  const winningTeam = isDraw
    ? [...yourCurrentTeam, ...opponentCurrentTeam]
    : isPlayerWinner
    ? yourCurrentTeam
    : opponentCurrentTeam;
  const titleText = isDraw ? "DRAW" : isPlayerWinner ? "VICTORY" : "DEFEAT";
  const titleColor = isDraw
    ? "#60a5fa"
    : isPlayerWinner
    ? "#fbbf24"
    : "#ef4444";
  const subTitleColor = isDraw
    ? "#dbeafe"
    : isPlayerWinner
    ? "#fef3c7"
    : "#fee2e2";
  const bgGradient = isDraw
    ? "radial-gradient(circle at center, rgba(96, 165, 250, 0.3) 0%, rgba(0,0,0,0.8) 100%)"
    : isPlayerWinner
    ? "radial-gradient(circle at center, rgba(251, 191, 36, 0.3) 0%, rgba(0,0,0,0.8) 100%)"
    : "radial-gradient(circle at center, rgba(239, 68, 68, 0.3) 0%, rgba(0,0,0,0.8) 100%)";

  return (
    <div className="absolute inset-0 z-[110] overflow-hidden select-none flex flex-col items-center justify-center">
      {/* Background Overlay */}
      <div
        className={`absolute inset-0 transition-all duration-1000 ${
          showImpact ? "opacity-100" : "opacity-0"
        }`}
        style={{
          background: bgGradient,
          backdropFilter: "blur(8px)",
        }}
      />

      {/* VFX Container - Relative for positioning */}
      <div className="absolute inset-0 overflow-hidden">
        {showImpact && isPlayerWinner && !isDraw && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <AnimatedVFX
              id="victory-stars"
              spriteUrl="/assets/spells/vfx/stars_effect_000__40.png"
              position={{ x: 0, y: 0 }}
              style={{ transform: "scale(3)" }}
              loops={3}
              speed={0.5}
            />
          </div>
        )}

        {showImpact && isPlayerWinner && !isDraw && (
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <AnimatedVFX
              id="victory-sparkles"
              spriteUrl="/assets/spells/vfx/self_sparkles_006__50.png"
              position={{ x: 0, y: 0 }}
              style={{ transform: "scale(4)" }}
              loops={3}
              speed={0.5}
            />
          </div>
        )}

        {showImpact && !isPlayerWinner && !isDraw && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <AnimatedVFX
              id="defeat-impact"
              spriteUrl="/assets/spells/impact/impfx1_toon_impact_B_large_white.png"
              position={{ x: 0, y: 0 }}
              style={{
                transform: "scale(4)",
                filter: "grayscale(100%) brightness(0.5)",
              }}
              loops={1}
              speed={0.5}
            />
          </div>
        )}

        {showImpact && isDraw && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <AnimatedVFX
              id="draw-impact"
              spriteUrl="/assets/spells/vfx/stars_effect_000__40.png"
              position={{ x: 0, y: 0 }}
              style={{
                transform: "scale(3)",
                filter: "hue-rotate(200deg)",
              }}
              loops={3}
              speed={0.5}
            />
          </div>
        )}
      </div>

      {/* Content Container */}
      <div
        className={`relative z-10 flex flex-col items-center gap-3 w-full mx-4 transition-all duration-500 ${
          showImpact ? "scale-100 opacity-100" : "scale-50 opacity-0"
        }`}
      >
        {/* Title */}
        <div className="flex flex-col items-center gap-[-16px]">
          <StrokedText
            strokeWidth={16}
            fontSize={isXsScreen() ? 32 : 48}
            fillColor={titleColor}
            strokeColor="#000"
            className="drop-shadow-[0_0_30px_rgba(0,0,0,0.8)] tracking-wider"
          >
            {titleText}
          </StrokedText>

          <StrokedText
            strokeWidth={8}
            fontSize={32}
            fillColor={subTitleColor}
            strokeColor="#000"
          >
            {isDraw
              ? "Both teams fell!"
              : isPlayerWinner
              ? `${winnerName} Win!`
              : `${winnerName} Wins...`}
          </StrokedText>
        </div>

        {/* Winner Team Display */}
        <div className="flex items-end justify-center gap-16 h-24">
          {winningTeam?.slice(0, 3).map((pokemon, idx) => (
            <div
              key={pokemon.id}
              className={`transition-all duration-500 delay-${idx * 200} ${
                showImpact
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-20"
              }`}
            >
              <div className="relative">
                <PokemonSprite
                  pokemon={pokemon}
                  style={{
                    scale: idx === 1 ? 2.75 : 2.25,
                    filter: isPlayerWinner
                      ? "brightness(1.1)"
                      : "grayscale(0.5) brightness(0.8)",
                  }}
                  className="drop-shadow-2xl"
                  direction={Direction.S}
                  showShadow={true}
                />
                {/* MVP Crown for middle pokemon? Just visual flair */}
                {idx === 1 && isPlayerWinner && (
                  <div className="absolute -top-36 left-1/2 -translate-x-1/2 animate-bounce">
                    <StrokedText
                      strokeWidth={4}
                      fontSize={48}
                      fillColor="#fbbf24"
                    >
                      👑
                    </StrokedText>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Score */}
        <div className="grid grid-cols-2 items-center gap-8 bg-black/40 px-6 py-2 rounded-xl border-2 border-white/10 backdrop-blur-md">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="flex flex-row items-center justify-center gap-2">
              {playerCountry && (
                <CountryFlag countryCode={playerCountry} size="sm" />
              )}
              <span className="text-sm text-pk-gray-4 font-bold uppercase tracking-widest mb-1">
                {username}
              </span>
            </div>
            <StrokedText
              strokeWidth={6}
              fontSize={36}
              fillColor={
                isDraw ? "#60a5fa" : isPlayerWinner ? "#fbbf24" : "#fff"
              }
            >
              {playerScore.toString()}
            </StrokedText>
          </div>
          <div className="h-18 w-[2px] bg-white/20 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="flex flex-row items-center gap-2">
              <span className="text-sm text-pk-gray-4 font-bold uppercase tracking-widest mb-1">
                {duelState.opponent_player?.username || "Opponent"}
              </span>
              {duelState.opponent_player?.country && (
                <CountryFlag
                  countryCode={duelState.opponent_player?.country}
                  size="sm"
                />
              )}
            </div>
            <StrokedText
              strokeWidth={6}
              fontSize={36}
              fillColor={
                isDraw ? "#60a5fa" : !isPlayerWinner ? "#ef4444" : "#fff"
              }
            >
              {opponentScore.toString()}
            </StrokedText>
          </div>
        </div>

        {/* Battle Stats */}
        {pokemonStats.stats.length > 0 && (
          <div className=" bg-black/40 px-2 py-1.5 rounded-xl border-2 border-white/10 backdrop-blur-md mx-2 w-full">
            <div className="grid grid-cols-2 space-y-4 w-full">
              {/* Blue Team (Player) */}
              <div className="grid grid-cols-1 gap-2 w-full">
                {pokemonStats.stats
                  .filter(({ isPlayerPokemon }) => isPlayerPokemon)
                  .map(({ pokemon, stats, isPlayerPokemon }) => {
                    const getBarWidth = (
                      value: number,
                      statType: keyof typeof pokemonStats.minMax,
                    ) => {
                      const { min, max } = pokemonStats.minMax[statType];
                      const range = max - min;
                      if (range === 0) return value > 0 ? 100 : 0;
                      return Math.max(
                        0,
                        Math.min(100, ((value - min) / range) * 100),
                      );
                    };

                    return (
                      <div
                        key={pokemon.id}
                        className={`flex items-center gap-2 p-2 rounded-lg h-16 ${
                          isPlayerPokemon ? "bg-blue-500/20" : "bg-red-500/20"
                        }`}
                      >
                        <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center">
                          <PokemonSprite
                            pokemon={pokemon}
                            style={{ scale: 1.2 }}
                            className="drop-shadow-md"
                            direction={Direction.S}
                            showShadow={false}
                          />
                        </div>

                        <div className="flex-1 min-w-0 flex flex-col justify-between h-full">
                          <div className="space-y-0.5 text-xs">
                            <div className="flex items-center gap-1">
                              <span className="text-pk-orange-4 w-6">DMG</span>
                              <div className="flex-1 bg-black/50 rounded-full h-5 overflow-hidden relative">
                                <div
                                  className="bg-pk-orange-4 h-full transition-all duration-500"
                                  style={{
                                    width: `${getBarWidth(
                                      stats.damage_done,
                                      "damage_done",
                                    )}%`,
                                  }}
                                />
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <span className="text-white text-sm font-mono font-bold drop-shadow-sm">
                                    {stats.damage_done.toFixed(0)}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-1">
                              <span className="text-pk-green-4 w-6">HEAL</span>
                              <div className="flex-1 bg-black/50 rounded-full h-5 overflow-hidden relative">
                                <div
                                  className="bg-pk-green-4 h-full transition-all duration-500"
                                  style={{
                                    width: `${getBarWidth(
                                      stats.healing_done,
                                      "healing_done",
                                    )}%`,
                                  }}
                                />
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <span className="text-white text-sm font-mono font-bold drop-shadow-sm">
                                    {stats.healing_done.toFixed(0)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>

              {/* Red Team (Opponent) */}
              <div className="grid grid-cols-1 gap-2 w-full">
                {pokemonStats.stats
                  .filter(({ isPlayerPokemon }) => !isPlayerPokemon)
                  .map(({ pokemon, stats, isPlayerPokemon }) => {
                    const getBarWidth = (
                      value: number,
                      statType: keyof typeof pokemonStats.minMax,
                    ) => {
                      const { min, max } = pokemonStats.minMax[statType];
                      const range = max - min;
                      if (range === 0) return value > 0 ? 100 : 0;
                      return Math.max(
                        0,
                        Math.min(100, ((value - min) / range) * 100),
                      );
                    };

                    return (
                      <div
                        key={pokemon.id}
                        className={`flex items-center gap-2 p-2 rounded-lg h-16 ${
                          isPlayerPokemon ? "bg-blue-500/20" : "bg-red-500/20"
                        }`}
                      >
                        <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center">
                          <PokemonSprite
                            pokemon={pokemon}
                            style={{ scale: 1.2 }}
                            className="drop-shadow-md"
                            direction={Direction.S}
                            showShadow={false}
                          />
                        </div>

                        <div className="flex-1 min-w-0 flex flex-col justify-between h-full">
                          <div className="space-y-0.5 text-xs">
                            <div className="flex items-center gap-1">
                              <span className="text-pk-orange-4 w-6">DMG</span>
                              <div className="flex-1 bg-black/50 rounded-full h-5 overflow-hidden relative">
                                <div
                                  className="bg-pk-orange-4 h-full transition-all duration-500"
                                  style={{
                                    width: `${getBarWidth(
                                      stats.damage_done,
                                      "damage_done",
                                    )}%`,
                                  }}
                                />
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <span className="text-white text-sm font-mono font-bold drop-shadow-sm">
                                    {stats.damage_done.toFixed(0)}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-1">
                              <span className="text-pk-green-4 w-6">HEAL</span>
                              <div className="flex-1 bg-black/50 rounded-full h-5 overflow-hidden relative">
                                <div
                                  className="bg-pk-green-4 h-full transition-all duration-500"
                                  style={{
                                    width: `${getBarWidth(
                                      stats.healing_done,
                                      "healing_done",
                                    )}%`,
                                  }}
                                />
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <span className="text-white text-sm font-mono font-bold drop-shadow-sm">
                                    {stats.healing_done.toFixed(0)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

        {/* Waiting Indicator */}
        <div
          className={` transition-opacity duration-500 ${
            phase === "waiting" ? "opacity-100" : "opacity-0"
          }`}
        >
          <div className="flex items-center gap-3 bg-black/60 px-6 py-2 rounded-full">
            <div
              className="w-2 h-2 bg-white rounded-full animate-bounce"
              style={{ animationDelay: "0ms" }}
            />
            <div
              className="w-2 h-2 bg-white rounded-full animate-bounce"
              style={{ animationDelay: "150ms" }}
            />
            <div
              className="w-2 h-2 bg-white rounded-full animate-bounce"
              style={{ animationDelay: "300ms" }}
            />
            <span className="text-sm text-white font-medium tracking-widest uppercase ml-2">
              Waiting for next round
            </span>
          </div>
        </div>
      </div>

      {/* Scanlines */}
      <div
        className="absolute inset-0 pointer-events-none opacity-10 z-50"
        style={{
          background:
            "linear-gradient(to bottom, transparent 50%, rgba(0,0,0,0.5) 50%)",
          backgroundSize: "100% 4px",
        }}
      />
    </div>
  );
};
